import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { currency } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import FeatureGate from "@/components/FeatureGate";

const STAGES = ["prospect", "contacted", "qualified", "negotiating", "won", "lost"];

function CRMInner() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", status: "new", pipeline_stage: "prospect", deal_value: "", notes: "" });

  const load = () => api.get("/crm/leads").then((r) => setLeads(r.data.items || []));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/crm/leads", { ...form, deal_value: form.deal_value ? parseFloat(form.deal_value) : null });
    setOpen(false);
    setForm({ name: "", company: "", phone: "", email: "", status: "new", pipeline_stage: "prospect", deal_value: "", notes: "" });
    load();
    toast.success("Lead added");
  };

  const updateStage = async (id, stage) => {
    await api.patch(`/crm/leads/${id}`, { pipeline_stage: stage });
    load();
  };

  const del = async (id) => {
    await api.delete(`/crm/leads/${id}`);
    load();
    toast.success("Removed");
  };

  const byStage = (s) => leads.filter(l => l.pipeline_stage === s);
  const totalValue = leads.reduce((a, l) => a + (l.deal_value || 0), 0);

  return (
    <div className="space-y-5" data-testid="crm-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Lead CRM</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Pipeline · <span className="text-[#d4a574]">{currency(totalValue)}</span></h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-lead-btn" className="flex items-center gap-2 px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988]"><Plus size={14} /> New Lead</button>
          </DialogTrigger>
          <DialogContent className="bg-[#1c1712] border-[#33291f] text-[#f4ede2]">
            <DialogHeader><DialogTitle className="font-display">Add Lead</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3" data-testid="new-lead-form">
              <Input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" className="bg-[#241d16] border-[#33291f]" />
              <Input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
                <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Deal value ($)" type="number" value={form.deal_value} onChange={(e) => setForm({ ...form, deal_value: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
                <Select value={form.pipeline_stage} onValueChange={(v) => setForm({ ...form, pipeline_stage: v })}>
                  <SelectTrigger className="bg-[#241d16] border-[#33291f]"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#241d16] border-[#33291f]" />
              <button type="submit" data-testid="lead-submit" className="w-full py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold">Create Lead</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pipeline board */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto">
        {STAGES.map((stage) => (
          <div key={stage} className="border border-[#33291f] rounded-lg bg-[#1c1712] p-3 min-w-[200px]" data-testid={`crm-column-${stage}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#d4a574] font-semibold">{stage}</div>
              <div className="text-[10px] font-mono text-[#7a6d5a]">{byStage(stage).length}</div>
            </div>
            <div className="space-y-2">
              {byStage(stage).map((l) => (
                <div key={l.id} data-testid={`lead-${l.id}`} className="p-3 rounded border border-[#33291f] bg-[#241d16] hover:border-[#d4a574]/40">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold truncate">{l.name}</div>
                      {l.company && <div className="text-[11px] text-[#7a6d5a] truncate">{l.company}</div>}
                    </div>
                    <button onClick={() => del(l.id)} className="text-[#7a6d5a] hover:text-[#f87171]"><Trash2 size={12} /></button>
                  </div>
                  {l.deal_value && <div className="font-mono text-[12px] text-[#d4a574] mt-2">{currency(l.deal_value)}</div>}
                  <Select value={l.pipeline_stage} onValueChange={(v) => updateStage(l.id, v)}>
                    <SelectTrigger className="h-7 mt-2 bg-[#14100c] border-[#33291f] text-[10px] uppercase"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {leads.length === 0 && (
        <div className="text-center py-16 text-[#7a6d5a] border border-dashed border-[#33291f] rounded-lg">
          No leads yet. Add your first from an opportunity detail page or click "New Lead".
        </div>
      )}
    </div>
  );
}

export default function CRMPage() {
  return (
    <FeatureGate feature="crm" title="Lead CRM is a premium feature" blurb="Manage leads across a 6-stage pipeline, track deal values, and log follow-ups. Included in Broker, Enterprise, and Institutional plans.">
      <CRMInner />
    </FeatureGate>
  );
}
